import type { ParticipantStatus, PublicState } from './types';

/**
 * The seven things a participant's phone can be showing.
 *
 * There is no "loading" member: loading is the absence of an answer from
 * {@link screenFor}, which cannot be computed until the first fetch returns.
 */
export type Screen =
  'pre-not-yet' | 'pre-form' | 'holding' | 'post-form' | 'done' | 'pre-missed' | 'post-closed';

/**
 * Decide what a participant should be looking at, given the stage the
 * organizer has opened and what this participant has already submitted.
 *
 * Pure, total, and the single authority on the question — the browser calls it
 * on every poll rather than tracking a screen of its own, so a phone that
 * slept through a stage change catches up by recomputing rather than by
 * following a transition it missed. Every branch has a test.
 *
 * Two orderings here are deliberate:
 *
 * - **Having submitted outranks what is open.** Somebody who has answered the
 *   post stage sees `done` even if the organizer reopens the pre stage. The
 *   answers are sealed, so there is nothing an open stage could offer them.
 *
 * - **Missing the pre stage is terminal for the day, but not for the page.**
 *   A participant who first scans after the pre stage has closed is told so
 *   (`pre-missed`) rather than left waiting for a post stage they could never
 *   contribute a comparison to. The browser keeps polling behind that message,
 *   so if the organizer reopens the pre stage — having closed it by accident,
 *   which is the likeliest reason it would ever reopen — the form appears
 *   without anybody having to rescan.
 */
export function screenFor(state: PublicState, status: ParticipantStatus): Screen {
  if (status.postSubmitted) return 'done';

  if (status.preSubmitted) {
    if (state.post.open) return 'post-form';
    if (state.post.everOpened) return 'post-closed';
    return 'holding';
  }

  if (state.pre.open) return 'pre-form';
  // A pre stage that has never opened is a survey that has not started, whatever
  // the organizer has done with the post stage. Waiting is the honest answer,
  // and it is the one that recovers on its own.
  if (!state.pre.everOpened) return 'pre-not-yet';
  return 'pre-missed';
}

/**
 * The screen a participant on the post form moves to when they choose the
 * discussion over the questions. Not a member of {@link Screen}: it is not
 * computed from server state but chosen on the phone, and the form stays
 * built underneath it so "back" costs nothing.
 */
export type MessageScreen = Exclude<Screen, 'pre-form' | 'post-form'> | 'post-skip';

/** How a screen gets the participant to Sway, if it does at all. */
export type SwayOffer = 'handoff' | 'link' | 'none';

/**
 * Whether this screen sends people on to Sway, and how.
 *
 * The discussion is for everyone who was in the room, whether or not they
 * answered anything, so it is offered from the moment the post-event stage
 * has opened on every screen a participant can be looking at then — the done
 * screen, the closed-post screen, the screen that turned a latecomer away
 * from the questions, and the post form itself for somebody who would rather
 * not answer. Before then there is nothing to send anybody to.
 *
 * `handoff` is an account made and a sign-in link; `link` is the plain Sway
 * link, for a deployment that does not ask for addresses.
 */
export function swayOffer(screen: MessageScreen, state: PublicState): SwayOffer {
  const opened = state.post.everOpened;
  const reachable =
    screen === 'done' ||
    screen === 'post-closed' ||
    screen === 'post-skip' ||
    (screen === 'pre-missed' && opened);
  if (!reachable || !opened) return 'none';
  if (state.config.emailStage !== 'none') return 'handoff';
  return state.config.swayUrl === '' ? 'none' : 'link';
}

/** Screens whose only way forward is the organizer changing something. */
const WAITING_SCREENS: ReadonlySet<Screen> = new Set<Screen>([
  'pre-not-yet',
  'holding',
  'pre-missed',
  'post-closed',
]);

/**
 * Whether the browser should keep asking the server what has changed.
 *
 * A form does not poll: replacing the screen under somebody halfway through
 * answering would throw away their work. `done` does not poll either — there
 * is nothing left to wait for.
 */
export function shouldPoll(screen: Screen): boolean {
  return WAITING_SCREENS.has(screen);
}
